import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supplierService } from '../lib/firebaseService';
import { t } from '../utils/translation';
import { X, Loader2 } from 'lucide-react';
import '../styles/components-extended.css';

export default function SupplierModal({ isOpen, onClose, supplier }) {
    const queryClient = useQueryClient();
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

    useEffect(() => {
        if (isOpen) {
            reset(supplier || { name: '', contactName: '', email: '', phone: '' });
        }
    }, [isOpen, supplier, reset]);

    const mutation = useMutation({
        mutationFn: (data) => supplier ? supplierService.updateSupplier(supplier.id, data) : supplierService.addSupplier(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['suppliers']);
            onClose();
        }
    });

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <div className="modal-header flex items-center justify-between">
                    <div>
                        <h3 className="modal-title text-2xl">
                            {supplier ? t('SUPPLIERS.MODAL.EDIT_TITLE') : t('SUPPLIERS.MODAL.ADD_TITLE')}
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                            {supplier ? t('SUPPLIERS_MODAL.EDIT_DESC') : t('SUPPLIERS_MODAL.ADD_DESC')}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="flex flex-col">
                    <div className="modal-body space-y-5">
                        <div className="form-group">
                            <label className="form-label">{t('SUPPLIERS.MODAL.COMPANY')}</label>
                            <input
                                className="input py-3"
                                placeholder="e.g. Metro Cash & Carry"
                                {...register('name', { required: true })}
                            />
                            {errors.name && <span className="text-red-500 text-xs mt-1">{t('COMMON.REQUIRED')}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">{t('SUPPLIERS.MODAL.CONTACT')}</label>
                            <input
                                className="input py-3"
                                placeholder="e.g. John Doe"
                                {...register('contactName')}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-5">
                            <div className="form-group">
                                <label className="form-label">{t('SUPPLIERS.MODAL.EMAIL')}</label>
                                <input
                                    className="input py-3"
                                    placeholder="orders@metro.com"
                                    {...register('email')}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('SUPPLIERS.MODAL.PHONE')}</label>
                                <input
                                    className="input py-3"
                                    placeholder="+33..."
                                    {...register('phone')}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="btn btn-secondary px-6">{t('COMMON.CANCEL')}</button>
                        <button type="submit" className="btn btn-primary px-8" disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                            {t('COMMON.SAVE')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
